import { Dictionary } from '@reduxjs/toolkit';
import { useMemo } from 'react';

interface IExploreComputedItems<T> {
    limit?: number;
    useEntities: () => Dictionary<T>;
    useByPage: () => any;
    useTotal: () => any;
    usePages: () => any;
}

export function useExploreComputedItems<T>({
    limit = 12,
    useEntities,
    useByPage,
    useTotal,
    usePages,
}: IExploreComputedItems<T | null>) {
    const entities = useEntities();
    const byPage = useByPage();
    const total = useTotal();
    const storePages = usePages();

    return useMemo(() => {
        if (!storePages.length) {
            return [];
        }
        const maxFetchedPage: number = Math.max(...storePages);
        // page on BE starts from 0, so we need to add 1.
        const pagesLength = maxFetchedPage + 1;
        const pages = Array.from({ length: pagesLength }).map((_, i) => i);

        if (!total) {
            return [];
        }

        const items = pages.reduce((acum, page) => {
            if (!byPage[page]) {
                const stubsArray = Array.from({ length: limit }).map(() => null);
                acum = acum.concat(...stubsArray);
            } else {
                const items = byPage[page].map((id: string) => entities[id] ?? null);
                acum = acum.concat(...items);
            }
            return acum;
        }, new Array<T | null>());

        if (items.length > total) {
            return items.slice(0, total);
        }

        return items;
    }, [byPage, total, storePages, entities, limit]);
}